import React from 'react';
import ReactMarkdown from 'react-markdown';

import { Flex, Link } from '@raystack/apsara';

import { CustomMarkdownRenderers } from '@src/components/CoPilot/CustomRenderMdElements';

import { CustomTable } from './CustomTable';

export interface DataRecord {
  [key: string]: string | number;
}

const transformKeys = (data: DataRecord[]) => {
  return data.map(record => {
    const transformedRecord: DataRecord = {};

    Object.keys(record).forEach(key => {
      // Remove any single or double quotes from the key
      const cleanKey = key.replace(/['"]+/g, ''); // Removes single or double quotes
      transformedRecord[cleanKey] = record[key];
    });

    return transformedRecord;
  });
};

const parseMarkdownTable = (markdownTable: string) => {
  const lines = markdownTable.trim().split('\n');

  // Extract headers from the first line after the table start
  const headers = lines[0]
    .split('|')
    .slice(1, -1)
    .map(header => header.trim().replace(/\*\*/g, '').replace(/['"]+/g, '')); // Remove ** and quotes from headers

  // Skip the separator line and parse the rest as rows
  const rows = lines.slice(2).map(
    line =>
      line
        .split('|')
        .slice(1, -1)
        .map(cell => cell.trim().replace(/\*\*/g, '')) // Clean up cell values
  );

  // Create data records for the table
  const data: DataRecord[] = rows.map(row => {
    const record: DataRecord = {};
    headers.forEach((header, index) => {
      record[header] = row[index] || '';
    });
    return record;
  });

  // Clean column headers and their corresponding id values
  const cleanedColumns = headers.map(header => {
    const id = header.replace(/['"]+/g, ''); // Remove both single and double quotes from the column id
    const Header = header.replace(/['"]+/g, ''); // Remove quotes from header as well
    return {
      header: Header,
      accessorKey: id
    };
  });

  // Return columns and data for the table
  return {
    columns: cleanedColumns,
    data
  };
};

const renderCitation = (citation: string) => {
  const citationRegex = /\[(.*?)\]\((https?:\/\/[^\s]+)\)/;
  const match = citation.match(citationRegex);

  if (match) {
    const text = match[1];
    const url = match[2];
    return (
      <Link
        href={url}
        underline
        external
        variant='accent'
        data-test-id='citationslinks'
      >
        {text}
      </Link>
    );
  }

  return citation;
};

export const MarkdownTableRenderer: React.FC<{ content: string }> = ({
  content
}) => {
  const tableStartIndex = content?.indexOf('\n|');
  const hasTable = tableStartIndex !== -1;

  const textContent = hasTable
    ? content?.slice(0, tableStartIndex).trim()
    : content;
  const tableContent = hasTable ? content?.slice(tableStartIndex).trim() : null;

  const tableData = tableContent ? parseMarkdownTable(tableContent) : null;
  const transformedData = tableData?.data ? transformKeys(tableData.data) : [];

  // Apply the renderCitation helper for any citation field
  const enhancedData = transformedData
    .map(row => {
      if (row.Citation) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        row.Citation = renderCitation(row.Citation as string);
      }
      return row;
    })
    .filter(row => {
      // Filter out rows where all values are empty
      return Object.values(row).some(value => value !== '');
    });

  return (
    <Flex direction='column' gap='medium'>
      <ReactMarkdown components={CustomMarkdownRenderers}>
        {textContent}
      </ReactMarkdown>
      {tableData && (
        <CustomTable columns={tableData.columns} data={enhancedData} />
      )}
    </Flex>
  );
};
